<script setup lang="ts">
import { useRouteParams } from "@vueuse/router";
import type { Ref } from "vue";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";

import BaseButton from "@/common/components/BaseButton.vue";
import BaseIcon from "@/common/components/icons/BaseIcon.vue";
import { formatDate } from "@/common/utils/dateUtils";
import { startCase } from "@/common/utils/startCase";
import {
  useGetKitmanInboundReportByIdQuery,
  useSubmitKitmanInboundReportMutation,
} from "@/inbound/api/queries";
import AnonymousLayout from "@/layouts/AnonymousLayout.vue";

definePage({
  meta: {
    layout: AnonymousLayout,
  },
});

const { t } = useI18n();
const eventId = useRouteParams("eventId") as Ref<string>;
const reportId = useRouteParams("reportId") as Ref<string>;

const { data: kitmanInboundReport, isPending: isReportLoading } =
  useGetKitmanInboundReportByIdQuery(reportId);

const { mutateAsync: submitKitmanInboundReport } =
  useSubmitKitmanInboundReportMutation();

const router = useRouter();
const onSubmit = async () => {
  await submitKitmanInboundReport(reportId.value);

  router.push({
    path: `/events/${eventId.value}/outbound-report/${reportId.value}/submitted`,
  });
};
</script>

<template>
  <div>
    <div>
      <ContentLoader
        v-if="isReportLoading"
        view-box="0 0 200 200"
      />
      <div class="bg-gray-100 pb-3">
        <div
          v-if="kitmanInboundReport"
          class="p-3 bg-white"
        >
          <h1 class="font-bold text-xl py-1">
            {{ t("inboundReport.productCheckList.title") }}
          </h1>
          <p class="text-gray-400">{{ kitmanInboundReport?.name }}</p>
          <p class="text-gray-400 pt-1">
            {{ formatDate(kitmanInboundReport?.date) }}
          </p>
        </div>
      </div>
      <div class="bg-white p-3 pb-28">
        <div v-if="kitmanInboundReport?.products.length === 0">
          <h2 class="text-gray-500 py-2 text-center">
            {{ t("inboundReport.productCheckList.noProducts") }}
          </h2>
        </div>
        <div v-else>
          <h2 class="font-semibold text-lg py-2">
            {{ t("inboundReport.productCheckList.subTitle") }}
          </h2>
          <div
            v-for="(item, index) in kitmanInboundReport?.products"
            :key="index"
          >
            <RouterLink
              :to="`/events/${eventId}/outbound-report/${reportId}/products/update/${item.id}`"
            >
              <div class="py-1 border-b">
                <div class="flex justify-between">
                  <div class="flex">
                    <p class="font-bold pr-2 w-10">{{ item.number }}</p>
                    <p class="font-bold flex flex-wrap">{{ item.name }}</p>
                  </div>
                  <div class="flex">
                    <p
                      class="text-sm bg-gray-100 rounded-lg px-2 text-gray-500"
                    >
                      {{
                        startCase(
                          t(
                            `inboundReport.productCheckList.availability.${item.availability}`,
                          ),
                        )
                      }}
                    </p>
                    <RouterLink
                      :to="`/events/${eventId}/outbound-report/${reportId}/products/update/${item.id}`"
                    >
                      <div class="pl-2">
                        <BaseIcon
                          name="heroicons:pencil"
                          class="icon-style size-4 text-indigo-600"
                          aria-hidden="true"
                        />
                      </div>
                    </RouterLink>
                  </div>
                </div>
                <div class="flex justify-start pt-1">
                  <p
                    v-if="item.wornDuring"
                    class="text-gray-500 text-sm pr-2"
                  >
                    {{
                      startCase(
                        t(
                          `inboundReport.productCheckList.wornDuring.${item.wornDuring}`,
                        ),
                      )
                    }}
                  </p>
                  <p class="text-gray-500 text-sm pr-2 h-fit">
                    {{
                      startCase(
                        t(
                          `inboundReport.productCheckList.condition.${item.condition}`,
                        ),
                      )
                    }}
                  </p>
                  <p class="text-gray-500 text-sm">
                    {{
                      startCase(
                        t(
                          item.isSigned
                            ? "inboundReport.productCheckList.signed"
                            : "inboundReport.productCheckList.unsigned",
                        ),
                      )
                    }}
                  </p>
                </div>
              </div>
            </RouterLink>
          </div>
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-1 fixed bottom-0 bg-white w-full p-3">
      <BaseButton
        variant="primary"
        label="Submit"
        @click="onSubmit"
      />
      <BaseButton
        label="Add product"
        variant="secondary"
        :path="`/events/${eventId}/outbound-report/${reportId}/products/add`"
      />
    </div>
  </div>
</template>
